// Mini-courbe SVG maison pour l'interface admin.
// Pas de dépendance : léger, contrôle total du style.
// Accepte une série `[{ date, value }]` et trace :
//   - un axe Y avec 3 ticks (min / milieu / max) + labels
//   - un axe X avec 3 dates (début / milieu / fin) au format court
//   - une grille horizontale très discrète
//   - l'aire remplie + la ligne + point terminal
import React from "react";

// Formate un nombre en concise (12.3k, 4.5M).
function formatShort(v) {
	if (v == null || isNaN(v)) return "";
	const n = Number(v);
	const abs = Math.abs(n);
	if (abs >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
	if (abs >= 1_000) return (n / 1_000).toFixed(1) + "k";
	if (Number.isInteger(n)) return String(n);
	return n.toFixed(1);
}

// Formate une date ISO (YYYY-MM-DD) en JJ/MM.
function formatDateShort(iso) {
	if (!iso) return "";
	const parts = iso.split("-");
	if (parts.length < 3) return iso;
	return `${parts[2]}/${parts[1]}`;
}

export default function Sparkline({
	data = [],
	color = "#30a49a",
	width = 600,
	height = 160,
	fill = true,
}) {
	const points = (data || []).filter((d) => d.value != null && !isNaN(d.value));
	if (points.length < 2) {
		return (
			<div style={{ width, height, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ink-soft)", fontSize: 10 }}>
				Pas assez de données
			</div>
		);
	}

	// Marges pour laisser la place aux ticks / labels
	const PAD_LEFT = 34;
	const PAD_RIGHT = 6;
	const PAD_TOP = 6;
	const PAD_BOTTOM = 16;
	const plotW = width - PAD_LEFT - PAD_RIGHT;
	const plotH = height - PAD_TOP - PAD_BOTTOM;

	const values = points.map((p) => Number(p.value));
	const rawMin = Math.min(...values);
	const rawMax = Math.max(...values);
	// Pas de valeurs négatives dans l'échelle : les KPI ne peuvent pas descendre
	// sous zéro (nombre d'inscrits, séances, revenus, réactions...).
	const min = Math.max(0, rawMin);
	// Si la série est plate, on force une plage > 0 pour éviter la division par 0.
	const max = rawMax === min ? min + 1 : rawMax;
	const range = max - min || 1;
	const stepX = plotW / (points.length - 1);

	const xy = points.map((p, i) => {
		const x = PAD_LEFT + i * stepX;
		const y = PAD_TOP + plotH - ((Number(p.value) - min) / range) * plotH;
		return { x, y, value: p.value, date: p.date };
	});

	const linePath = xy.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
	const areaPath = `${linePath} L${xy[xy.length - 1].x.toFixed(1)},${PAD_TOP + plotH} L${xy[0].x.toFixed(1)},${PAD_TOP + plotH} Z`;

	// 3 ticks Y : min, milieu, max
	const yTicks = [
		{ value: max, y: PAD_TOP },
		{ value: (min + max) / 2, y: PAD_TOP + plotH / 2 },
		{ value: min, y: PAD_TOP + plotH },
	];

	// 3 ticks X : première date, milieu, dernière
	const middleIdx = Math.floor((points.length - 1) / 2);
	const xTicks = [
		{ date: points[0].date, x: PAD_LEFT, anchor: "start" },
		{ date: points[middleIdx].date, x: PAD_LEFT + (middleIdx * stepX), anchor: "middle" },
		{ date: points[points.length - 1].date, x: PAD_LEFT + plotW, anchor: "end" },
	];

	return (
		<svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="admin-sparkline">
			{/* Grille horizontale très discrète */}
			{yTicks.map((t, i) => (
				<line
					key={`grid-${i}`}
					x1={PAD_LEFT}
					y1={t.y}
					x2={PAD_LEFT + plotW}
					y2={t.y}
					stroke="currentColor"
					strokeOpacity="0.08"
					strokeDasharray="2 3"
					strokeWidth="1"
				/>
			))}

			{/* Ticks Y — labels */}
			{yTicks.map((t, i) => (
				<text
					key={`ty-${i}`}
					x={PAD_LEFT - 4}
					y={t.y + 3}
					fontSize="9"
					fill="currentColor"
					fillOpacity="0.55"
					textAnchor="end"
					fontFamily="system-ui, -apple-system, sans-serif"
				>
					{formatShort(t.value)}
				</text>
			))}

			{/* Aire + ligne */}
			{fill && <path d={areaPath} fill={color} opacity="0.14" />}
			<path d={linePath} fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />

			{/* Point terminal mis en avant */}
			<circle
				cx={xy[xy.length - 1].x}
				cy={xy[xy.length - 1].y}
				r="2.6"
				fill={color}
			/>

			{/* Ticks X — labels de dates */}
			{xTicks.map((t, i) => (
				<text
					key={`tx-${i}`}
					x={t.x}
					y={height - 4}
					fontSize="9"
					fill="currentColor"
					fillOpacity="0.55"
					textAnchor={t.anchor}
					fontFamily="system-ui, -apple-system, sans-serif"
				>
					{formatDateShort(t.date)}
				</text>
			))}
		</svg>
	);
}
